import { Link } from "react-router-dom";
import { ArrowUpRight, Cpu, Bot, GitBranch, ShieldCheck, Sparkles, Database, Network, Clock } from "lucide-react";
import PageHero from "../components/site/PageHero";
import AIAutomation from "../components/site/AIAutomation";
import FinalCTA from "../components/site/FinalCTA";
import Problem from "../components/site/Problem";
import { usePageSEO, breadcrumb, serviceSchema } from "../hooks/usePageSEO";
import { BRAND, waLink } from "../brand";

const capabilities = [
  { icon: Bot, t: "Chatbots con IA", d: "Agentes con tu tono, catálogo y reglas comerciales reales." },
  { icon: GitBranch, t: "Automatización omnicanal", d: "WhatsApp, IG, FB, Web Chat, Email y SMS orquestados." },
  { icon: Database, t: "Integración con CRM", d: "HubSpot, Pipedrive, Notion, Google Sheets, ERPs custom." },
  { icon: Network, t: "Webhooks & APIs", d: "Conecto tu stack interno con tu adquisición y tu equipo." },
  { icon: Sparkles, t: "Calificación automática", d: "Scoring + reglas + IA: el sistema filtra curiosos antes del CRM." },
  { icon: Clock, t: "Atención 24/7", d: "Cero leads perdidos en madrugadas, festivos o pico de demanda." },
  { icon: Cpu, t: "Seguimiento automático", d: "Recordatorios, reactivación y nurture sin tocar nada manualmente." },
  { icon: ShieldCheck, t: "Escalamiento humano", d: "La IA pasa al equipo cuando importa. Sin perder contexto." },
];

export default function AutomatizacionIA() {
  usePageSEO({
    title: "Automatización IA para Empresas | Daos Ads",
    description:
      "Implemento agentes con IA, chatbots inteligentes y automatización omnicanal para empresas medianas y grandes. Respuesta < 1 min, 24/7, integrado con tu CRM y tus campañas. Implementación real, no estrategia.",
    path: "/automatizacion-ia",
    jsonLd: [
      breadcrumb([
        { name: "Inicio", path: "/" },
        { name: "Automatización IA", path: "/automatizacion-ia" },
      ]),
      serviceSchema({
        name: "Automatización con IA para empresas",
        description:
          "Agentes con IA, chatbots conversacionales, automatización omnicanal (WhatsApp, Instagram, Facebook, Web Chat, Email) e integraciones CRM para calificar y cerrar leads 24/7.",
        path: "/automatizacion-ia",
      }),
    ],
  });

  return (
    <main>
      <PageHero
        eyebrow="Automatización con IA"
        title="Una IA que vende, califica y"
        highlight="escala por ti"
        description="No bots básicos. Agentes con IA conectados a tu CRM, tus campañas y tu equipo comercial — operando 24/7 sin caer en la mediocridad de un chatbot genérico."
        rightSlot={
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#ff3b1f]/30 via-[#ff8a00]/20 to-transparent rounded-[2.2rem] blur-2xl" />
            <div className="relative h-full w-full rounded-[2rem] overflow-hidden border border-white/10 glow-soft">
              <img
                src={BRAND.images.aiBackground}
                alt="IA para automatización"
                className="absolute inset-0 w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 glass-strong rounded-2xl p-4">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#ff8a00] font-display">Stack core</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {["GPT", "Claude", "Make", "n8n", "Zapier", "Respond.io"].map((t) => (
                    <span key={t} className="text-[11px] px-2.5 py-1 rounded-full glass text-zinc-300">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }
      />

      {/* Reuse existing AI Automation block */}
      <AIAutomation />

      {/* Capabilities grid */}
      <section className="relative py-24">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Capacidades enterprise</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              Una capa de <span className="text-gradient">automatización</span> que tu equipo entiende.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {capabilities.map(({ icon: Icon, t, d }, i) => (
              <div key={t} className="reveal grad-border p-6 card-lift" style={{ transitionDelay: `${i * 40}ms` }}>
                <div className="w-11 h-11 rounded-xl glass grid place-items-center text-[#ff8a00] mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-white font-display font-semibold">{t}</div>
                <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain context (reuse Problem) */}
      <Problem />

      {/* Cross-links */}
      <section className="relative py-20">
        <div className="container-x">
          <div className="reveal max-w-2xl">
            <div className="eyebrow mb-5">Continúa explorando</div>
            <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
              La IA es <span className="text-gradient">una pieza</span> del sistema.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: "/respond-io", label: "Respond.io", desc: "Centralización omnicanal con IA conversacional." },
              { to: "/funnels", label: "Funnels", desc: "Tráfico → automatización → calificación → ventas." },
              { to: "/casos-de-exito", label: "Casos de éxito", desc: "Métricas y resultados de automatizaciones reales." },
            ].map((r) => (
              <Link key={r.to} to={r.to} className="reveal grad-border p-6 card-lift block">
                <div className="flex items-center justify-between">
                  <div className="text-white font-display font-semibold">{r.label}</div>
                  <ArrowUpRight className="w-5 h-5 text-zinc-400" />
                </div>
                <p className="mt-2 text-zinc-400 text-sm">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
